// Official OpenStreetMap Carto symbols (CC0), bundled from src/assets/osm-carto/<key>/<value>.svg
// (fetched with tools/osm-icons/fetch.sh). Exposed as plain path data so the 2D map (Path2D) and
// the AR renderer (canvas texture) can draw them in any colour and size without loading images.

export interface OsmIcon {
  /** OSM tag, e.g. 'leisure=slipway'. */
  tag: string;
  /** viewBox width/height (carto symbols are 14 x 14). */
  width: number;
  height: number;
  /** SVG path data of all <path> elements, drawn as one fill. */
  paths: string[];
}

const files = import.meta.glob<string>('./osm-carto/*/*.svg', { query: '?raw', import: 'default', eager: true });

/** Parse the subset of SVG used by carto symbols: a viewBox and <path d="..."> elements. */
export function parseOsmIconSvg(tag: string, svg: string): OsmIcon {
  const vb = /viewBox="([^"]+)"/.exec(svg)?.[1].trim().split(/[\s,]+/).map(Number);
  const width = vb && vb.length === 4 ? vb[2] : Number(/width="([\d.]+)"/.exec(svg)?.[1] ?? 14);
  const height = vb && vb.length === 4 ? vb[3] : Number(/height="([\d.]+)"/.exec(svg)?.[1] ?? 14);
  const paths = [...svg.matchAll(/<path\b[^>]*\sd="([^"]+)"/g)].map((m) => m[1]);
  return { tag, width, height, paths };
}

const icons = new Map<string, OsmIcon>();
for (const [file, svg] of Object.entries(files)) {
  const m = /osm-carto\/([^/]+)\/([^/]+)\.svg$/.exec(file);
  if (!m) continue;
  const tag = `${m[1]}=${m[2]}`;
  icons.set(tag, parseOsmIconSvg(tag, svg));
}

/** Icon for an OSM tag ('key=value'), or null if not bundled. */
export function osmIcon(tag: string): OsmIcon | null {
  return icons.get(tag) ?? null;
}

/** Colour carto uses for transport icons (slipway, ferry, …): `@transportation-icon`. */
export const OSM_TRANSPORT_ICON_COLOR = '#0092da';

const path2d = new WeakMap<OsmIcon, Path2D[]>();

/**
 * Draw `icon` centred at (cx, cy) with the given size (px of the longer viewBox side). Uses the
 * current fillStyle. Works for CanvasRenderingContext2D and OffscreenCanvasRenderingContext2D.
 */
export function drawOsmIcon(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  icon: OsmIcon,
  cx: number,
  cy: number,
  size: number,
): void {
  const k = size / Math.max(icon.width, icon.height);
  ctx.save();
  ctx.translate(cx - (icon.width * k) / 2, cy - (icon.height * k) / 2);
  ctx.scale(k, k);
  let p = path2d.get(icon);
  if (!p) path2d.set(icon, (p = icon.paths.map((d) => new Path2D(d))));
  for (const x of p) ctx.fill(x);
  ctx.restore();
}
